import React from "react";
import { useTranslation } from "react-i18next";
import Text, { TextVariant } from "@/components/ui/text";
import { formatCurrency } from "../consts";
import { useCurrencyConversion } from "@/services/hooks/useCurrencyConversion";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { formatNumber } from "@/utils/numbers";
import { getCurrentLocale } from "@/utils/locale";

interface CalculatedValuesFooterProps {
  mrrLocal: number | null;
  mrr: number | null;
  arrLocal: number | null;
  arr: number | null;
  cmrrLocal: number | null;
  cmrr: number | null;
  carrLocal: number | null;
  carr: number | null;
  acvLocal: number | null;
  acv: number | null;
  tcvLocal: number | null;
  tcv: number | null;
  currency?: string | null;
  showCommitted?: boolean;
}

interface ConvertedDisplayProps {
  amount: number | null;
  accountCurrencySymbol: string;
  variant?: TextVariant;
}

const ConvertedDisplay: React.FC<ConvertedDisplayProps> = ({
  amount,
  accountCurrencySymbol,
  variant = "bodyTertiary"
}) => {
  if (amount == null) return null;
  // Whole units only, matching ConvertedCurrency — this is an FX estimate, not the contracted amount.
  const { value } = formatNumber(amount, 0, true, false, false, getCurrentLocale());
  return (
    <Text variant={variant}>{`(${accountCurrencySymbol}${value})`}</Text>
  );
};

const CalculatedValuesFooter: React.FC<CalculatedValuesFooterProps> = ({
  tcvLocal,
  tcv,
  acvLocal,
  acv,
  mrrLocal,
  mrr,
  arrLocal,
  arr,
  cmrrLocal,
  cmrr,
  carrLocal,
  carr,
  currency,
  showCommitted = true
}) => {
  const { t } = useTranslation();
  const { isArr } = useArrMrr();

  // This footer sits directly under the revenue-item table, which prints the
  // record half qualified and the account half as the account's own symbol. It
  // used formatCurrency for both, so on a USD contract under a CAD account the
  // table read `USD1,000 ($1,350)` while this read `$1,000 (CA$1,350)` — the
  // same glyph standing for a different currency in two adjacent panels.
  // Undefaulted currency: formatCurrency resolves null itself, and defaulting
  // here would have the hook compare a contract that has no currency as USD.
  const {
    accountCurrencySymbol,
    recordCurrencySignOverride,
    showConversion
  } = useCurrencyConversion(currency);

  const recurringValue = isArr ? arrLocal : mrrLocal;
  const recurringConverted = isArr ? arr : mrr;
  const recurringLabelKey = isArr ? "contractTerms.arr" : "contractTerms.mrr";
  const recurringFullLabelKey = isArr
    ? "contractTerms.arrFull"
    : "contractTerms.mrrFull";

  const committedValue = isArr ? carrLocal : cmrrLocal;
  const committedConverted = isArr ? carr : cmrr;
  const committedLabelKey = isArr ? "contractTerms.carr" : "contractTerms.cmrr";
  const committedFullLabelKey = isArr
    ? "contractTerms.carrFull"
    : "contractTerms.cmrrFull";

  return (
    // No fill: the summary sits on whatever ground the page paints, so it reads
    // as a continuation of the table above rather than a second panel. The
    // table box tucked above it is `relative z-10`, so it — not this — paints
    // the 12px the negative margin overlaps.
    <div className="flex flex-col w-full -mt-3 pt-6 border border-border-border1 rounded-b-xl px-5 pb-2.5">
      <Text variant="bodyTertiary" className="mb-3">
        {t("contractTerms.calculatedValues")}
      </Text>
      <div className="flex items-center justify-between py-1">
        <div className="flex items-center gap-1">
          <Text variant="bodySecondary">{t(recurringLabelKey)}</Text>
          <Text variant="bodyTertiary">({t(recurringFullLabelKey)})</Text>
        </div>
        <div className="flex items-center gap-1">
          <Text variant="body" className="text-right">
            {formatCurrency(recurringValue ?? 0, currency, undefined, recordCurrencySignOverride)}
          </Text>
          {showConversion && (recurringValue ?? 0) > 0 && (
            <ConvertedDisplay
              amount={recurringConverted}
              accountCurrencySymbol={accountCurrencySymbol}
            />
          )}
        </div>
      </div>
      {showCommitted && (
        <div className="flex items-center justify-between py-1">
          <div className="flex items-center gap-1">
            <Text variant="bodySecondary">{t(committedLabelKey)}</Text>
            <Text variant="bodyTertiary">({t(committedFullLabelKey)})</Text>
          </div>
          <div className="flex items-center gap-1">
            <Text variant="body" className="text-right">
              {formatCurrency(committedValue ?? 0, currency, undefined, recordCurrencySignOverride)}
            </Text>
            {showConversion && (committedValue ?? 0) > 0 && (
              <ConvertedDisplay
                amount={committedConverted}
                accountCurrencySymbol={accountCurrencySymbol}
              />
            )}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between py-1">
        <div className="flex items-center gap-1">
          <Text variant="bodySecondary">{t("contractTerms.acv")}</Text>
          <Text variant="bodyTertiary">({t("contractTerms.acvFull")})</Text>
        </div>
        <div className="flex items-center gap-1">
          <Text variant="body" className="text-right">
            {formatCurrency(acvLocal ?? 0, currency, undefined, recordCurrencySignOverride)}
          </Text>
          {showConversion && (acvLocal ?? 0) > 0 && (
            <ConvertedDisplay amount={acv} accountCurrencySymbol={accountCurrencySymbol} />
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pb-1">
        <div className="flex items-center gap-1">
          <Text variant="bodySecondary">{t("contractTerms.tcv")}</Text>
          <Text variant="bodyTertiary">({t("contractTerms.tcvFull")})</Text>
        </div>
        <div className="flex items-baseline gap-1">
          <Text variant="lgSubtitle" className="font-semibold text-right">
            {formatCurrency(tcvLocal ?? 0, currency, undefined, recordCurrencySignOverride)}
          </Text>
          {showConversion && (tcvLocal ?? 0) > 0 && (
            <ConvertedDisplay
              amount={tcv}
              accountCurrencySymbol={accountCurrencySymbol}
              variant="lgSubtitleTertiary"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CalculatedValuesFooter;
